import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useState } from 'react'

import { usePeerGroups } from '../../providers/PeerGroups/PeerGroups.provider'
import { useSchedule } from '../../providers/Schedule/Schedule.provider'
import { themeColors } from '../../theme'
import { getUserInitials } from '../../utilities/HelperFunctions'
import { Modal } from '../Modal/Modal'
import {
  PatientOrProvider,
  PopOutUser,
} from '../PatientOrProviderDetails/types'

dayjs.extend(utc)

export interface UserDetailBlockProps {
  tabType?: string | null
  user: PopOutUser
  onUserClick?: () => void
  showRemovePatient?: boolean
}

export const UserDetailBlock = ({
  tabType,
  user: {
    name,
    photoUrl,
    providerRole,
    patientMrn,
    patientUserId,
    patientOrProvider,
    isModerator,
    isCoModerator,
    isJoined,
    attended,
    sessionId,
    sessionStartTime,
    sessionEndTime,
    activeDay,
  },
  onUserClick,
  showRemovePatient,
}: UserDetailBlockProps) => {
  const { pushPatientIntoPeerGroupSession } = usePeerGroups()
  const { get7dayAppointments } = useSchedule()

  const [openPushPatientModal, setOpenPushPatientModal] = useState(false)
  const [inProgress, setInProgress] = useState(false)

  const sessionStartTarget = dayjs.utc(sessionStartTime).local()
  const sessionEndTarget = dayjs.utc(sessionEndTime).local()

  // const isSessionDate = dayjs().isSame(sessionStartTarget, 'day')
  const sessionIsInPast = dayjs().isAfter(sessionEndTarget)
  const sessionIsOn = dayjs().isBetween(sessionStartTarget, sessionEndTarget)
  const sessionIsInFuture = dayjs().isBefore(sessionStartTarget)

  const renderPatientSessionStatusTag = () => {
    if (sessionIsOn) {
      if (isJoined) {
        return (
          <Typography
            sx={{
              fontSize: '12px',
              padding: '5px 8px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              color: themeColors.white,
              borderRadius: '5px',
              backgroundColor: themeColors.green,
            }}
          >
            In Session
          </Typography>
        )
      } else {
        return (
          <Button
            variant={isJoined ? 'outlined' : 'contained'}
            onClick={() =>
              setOpenPushPatientModal && setOpenPushPatientModal(true)
            }
            size='small'
            sx={{
              fontSize: '11px',
              padding: '4px 8px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
            }}
            disabled={isJoined}
          >
            Push Patient In
          </Button>
        )
      }
    } else if (sessionIsInPast) {
      return (
        <Typography
          sx={{
            fontSize: '12px',
            padding: '5px 8px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            color: themeColors.white,
            borderRadius: '5px',
            backgroundColor: attended ? themeColors.green : themeColors.red,
          }}
        >
          {attended ? 'Attended' : 'Not Attended'}
        </Typography>
      )
    } else {
      return null
    }
  }

  return (
    <Grid
      container
      sx={{ marginTop: '20px', maxWidth: '420px' }}
      justifyContent='flex-start'
      alignItems='center'
    >
      <Grid item xs={2.5} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Avatar
          sx={{
            bgcolor: themeColors.gray40,
            color: themeColors.white,
            width: 50,
            height: 50,
          }}
          alt={name}
          src={photoUrl}
        >
          {name ? getUserInitials(name) : ''}
        </Avatar>
      </Grid>
      <Grid
        item
        xs={4}
        sx={{
          marginLeft: '10px',
        }}
      >
        <Typography
          sx={{
            color: themeColors.grayblack,
            fontSize: '13px',
            fontWeight: '700',
            margin: 0,
          }}
        >
          {name}
        </Typography>
        {PatientOrProvider.Provider && (
          <Typography
            sx={{
              color: themeColors.blue,
              fontSize: '13px',
              fontWeight: '1000',
              margin: 0,
            }}
          >
            {isModerator ? 'Moderator' : ''}
            {isCoModerator ? 'Co-Moderator' : ''}
          </Typography>
        )}
        <Typography
          sx={{
            color: themeColors.gray40,
            fontSize: '13px',
            fontWeight: '700',
            margin: 0,
          }}
        >
          {patientOrProvider === PatientOrProvider.Provider
            ? providerRole
            : patientMrn}
        </Typography>
      </Grid>
      <Grid
        item
        xs={3}
        sx={{
          marginLeft: '10px',
        }}
      >
        <Box display={'flex'} flexDirection={'row'} alignItems={'flex-start'}>
          {showRemovePatient && (
            <Button
              variant='outlined'
              color='error'
              onClick={onUserClick}
              size='small'
              sx={{
                fontSize: '11px',
                padding: '4px 8px',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                margin: '0 8px',
              }}
            >
              Remove
            </Button>
          )}

          {/* patient attended / InSession/ NeedsToBePushedIn status tag*/}
          {tabType === 'patients' ? renderPatientSessionStatusTag() : null}
        </Box>
      </Grid>

      <Modal
        title={'Push Patient into Peer Group Session'}
        open={openPushPatientModal}
        setOpen={setOpenPushPatientModal}
        ModalContent={
          <Box sx={{ p: 2 }}>
            <Typography style={{ justifySelf: 'center', marginBottom: '10px' }}>
              {`Are you sure to push patient "${name}" into the session?`}
            </Typography>

            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant='outlined'
                onClick={() => setOpenPushPatientModal(false)}
                sx={{ margin: '0 10px' }}
                disabled={inProgress}
              >
                Cancel
              </Button>

              <Button
                variant='contained'
                onClick={async () => {
                  setInProgress(true)

                  patientUserId &&
                    sessionId &&
                    (await pushPatientIntoPeerGroupSession(
                      patientUserId,
                      sessionId
                    ))

                  activeDay && (await get7dayAppointments(activeDay))

                  setInProgress(false)
                  setOpenPushPatientModal(false)
                }}
                disabled={inProgress}
              >
                {inProgress ? (
                  <CircularProgress size={16} color='info' />
                ) : (
                  'Confirm'
                )}
              </Button>
            </Box>
          </Box>
        }
      />
    </Grid>
  )
}
